/** 누끼 한 요청이 쓸 수 있는 **시간 예산**.
 *
 * **앱과 서버가 같은 시간 예산을 각자 들고 있지 않게 하는 것이 이 파일의 일 전부다.**
 * 스키마가 아니라 시간이지만, 여기서 갈리면 **D1 에 행이 남는 방식이 달라진다.**
 *
 * ## 왜 생겼나 (2026-09-16 실기기 실측: `c72b5c13`, `bc167ca1`)
 *
 * 앱은 1장 기준 39초에 끊었고(`apps/mobile/src/lib/api.ts` 의 `processTimeoutMs`),
 * 서버는 컨테이너가 일어서기를 45초까지 기다렸다(`apps/api/src/lib/imaging-container.ts`
 * 의 `BOOT_DEADLINE_MS`). 그 6초 사이에 실기기가 두 번 들어갔다.
 *
 * **클라이언트가 먼저 끊으면 워커 실행이 취소된다.** 취소된 워커는 `markImageDone` 도
 * `markImageFailed` 도 쓰지 못한다. 남는 것은 `JobImage.status = 'PROCESSING'` 인데
 * `lastErrorCode` 와 `providerSucceededAt` 이 **둘 다 `NULL`** 인 행이다.
 * `docs/DATA_DICTIONARY.md` 19장이 "워커가 죽어 `PROCESSING` 에 갇힌 장"이라고 적어 둔
 * 바로 그 행이고, **죽은 이유가 이것이었다.** 19장은 갇힌 행을 되찾는 법(임대 만료 회수)을
 * 정했고, 이 파일은 **그 행이 처음부터 안 생기게 하는 쪽**이다.
 *
 * 즉 증상은 "느리다"가 아니다. **"정상 처리 중인데 클라이언트가 끊어 장이 갇히는 것"** 이다.
 * 그래서 이 파일이 지키는 부등식은 하나뿐이다.
 *
 * ```
 * 앱이 기다리는 시간  >  서버가 스스로 끊는 시간
 * ```
 *
 * ## 어떻게 지키나: 두 값을 나란히 적지 않는다
 *
 * **클라이언트 값을 서버 값에서 파생시킨다.** `cutoutClientTimeoutMs` 는 자기 숫자를
 * 갖지 않고 `cutoutServerBudgetMs` 를 부른 뒤 여유만 더한다. 두 값을 나란히 적어 두면
 * 공급자를 갈아끼울 때 한쪽만 따라오고, 그 순간 위 부등식이 **조용히** 뒤집힌다.
 * 실제로 그렇게 뒤집혀 있었고, 아무 로그도 남지 않았다.
 *
 * ## 고칠 자리는 아래 숫자 아홉 줄뿐이다 (2026-09-18: 셋이 늘었다. 2026-09-19: 둘 더)
 *
 * ⛔ **한때 이 자리에 "콜드 스타트만은 재 봤다"고 적혀 있었다. 지금은 거짓이다.** 그 9.0초는
 * **Cloudflare CPU 컨테이너 시절의 관측**이고, 지금 상대인 **Modal L4 GPU** 에서는 콜드
 * 스타트를 아무도 잰 적이 없다. 사정은 `CUTOUT_COLD_START_BUDGET_MS` 머리말에 있다.
 * **여기 옮겨 적지 않는다.** 두 벌이 되면 다음에 한쪽만 고쳐진다.
 *
 * ⚠️ **지금 공급자에서 나온 관측은 축소 호출 하나뿐이다**(`RENDITION_CALL_ESTIMATE_MS` 의
 * 2026-09-20 교정). **그 하나를 빼면 나머지 여덟 줄은 재 본 적이 없다.** 근거가 있는 값이
 * 아니라 **첫 실측 때 고칠 값**이고, 고칠 때 앱과 서버가 함께 움직이라고 한 파일에 모아 둔
 * 것이다. 호출부에서 이 숫자에 무엇을 더하거나 곱하지 마라.
 * 그 순간 고칠 자리가 다시 둘이 된다.
 *
 * ⚠️ **아홉 중 둘은 추정이 아니다.** `CUTOUT_ROUNDTRIPS_PER_IMAGE`(12)와
 * `CUTOUT_TAIL_ROUNDTRIPS`(5)는 `apps/api/src/routes/jobs.ts` 의 `await` 를 센 값이라
 * **코드가 바뀌면 틀리고, 안 바뀌면 맞다.** 실측으로 고칠 줄이 아니라 **코드를 따라가야 하는
 * 줄**이고, 그래서 둘 다 무엇을 셌는지를 표로 남겨 뒀다.
 *
 * ## 예산이 덮는 것은 공급자 호출만이 아니다 (2026-09-18 추가)
 *
 * 처음 판은 `공급자 몫` 만 예산했다. 그런데 장 하나를 돌리는 데 **공급자 밖에서 D1·R2 왕복이
 * 열 번** 더 돌고, 루프가 끝난 뒤에도 **다섯 번**이 남는다. 전부 `await` 라 직렬이고 전부
 * 셀러가 기다리는 시간이다. **그 몫이 예산 어디에도 없으면 서버는 자기 예산을 넘겨 끝나고,
 * 넘긴 만큼을 앱의 여유가 대신 물어야 한다.** 이 파일이 막으려던 갇힘이 거기서 돌아온다.
 *
 * ⚠️ **2026-09-19: 저장소 왕복이 아닌 것이 둘 붙었다.** 축소본을 만드는
 * `POST /v1/renditions` 와 산출물을 굽는 `POST /v1/compose` 는 상대가 D1·R2 가 아니라
 * **우리 컨테이너 CPU** 라 왕복 수에 세지 않고 `RENDITION_CALL_ESTIMATE_MS` ·
 * `COMPOSITION_CALL_ESTIMATE_MS` 로 따로 뗀다. 왜 한 숫자에 섞지 않았는지는 각 상수에
 * 적어 뒀다. ⛔ **둘을 한 상수로 다시 합치지 마라.** 크기가 다르고(전송 꼴부터 다르다)
 * **틀렸을 때 잃는 것이 다르다** — 축소가 끊기면 그림이 흐리고, 합성이 끊기면 **산출물이
 * 아예 없다**(`docs/DATA_DICTIONARY.md` 25.3).
 *
 * 그래서 **예산에서 먼저 뗀다**(`cutoutOverheadMs`). 앱의 여유를 늘리는 쪽이 아니다. 늘리면
 * 앱만 더 기다리고 **서버는 여전히 넘겨 돈다** . 갈림 둘 중 어느 쪽을 왜 골랐는지는
 * `cutoutOverheadMs` 머리말에 적어 뒀다.
 *
 * ⛔ **같은 회차에 나온 왕복 0.55~0.76초를 장당 시간으로 읽지 마라.** 그 왕복은
 * `/v1/compose`(합성)이고 `/v1/cutout`(222MB ONNX 추론)이 아니다. 합성에는 모델 추론이
 * 없다. **이것이 이 파일에서 가장 비싼 오해다** — 왜 콜드 스타트에는 그 회차의 값을 쓰면서
 * 장당 시간에는 못 쓰는지를 아래 두 상수에 각각 적어 뒀다. 읽지 않고 숫자만 옮기지 마라.
 *
 * **왜 갇힘의 원인이 아니라 예산이 여기 있나(2026-09-16 추가).** 그날 밝혀진 진짜 원인은
 * 느림이 아니라 **컨테이너 애플리케이션이 운영 네임스페이스에만 붙어 있고 개발 쪽엔 없던
 * 것**이었다. 개발 워커의 DO 가 닿을 컨테이너가 없어 호출이 응답 없이 매달렸다. 그것은
 * 배포 설정의 문제이고 이미 고쳐졌다(`imaging-container.ts` 의 `CONTAINER_NOT_ATTACHED`
 * 갈래가 그 상황을 이름으로 끊는다). **이 파일이 막는 갈래는 그것과 다르다** — 정상적으로
 * 처리 중인데 앱이 먼저 끊어 장이 갇히는 쪽이다. 원인이 하나 밝혀졌다고 이 부등식이 필요
 * 없어지지 않는다.
 *
 * ⛔ **서버가 이 예산에서 실제로 끊지 않으면 이 파일은 절반만 참이다.** 앱이 늦게 끊는 것은
 * 서버가 제 시각에 끝을 적을 때만 도움이 된다. 서버가 예산을 넘겨 계속 돌면 앱이
 * `예산 + 여유` 에서 끊고, 그러면 **장수만 커진 채로 같은 갇힘이 다시 난다.**
 * `cutoutServerBudgetMs` 는 서버가 **지켜야 하는 약속**이지 관측값이 아니다.
 *
 * ⛔ **이 값들은 벽시계 시간이다. Workers 의 CPU 시간이 아니다.** 대부분이 컨테이너 응답을
 * 기다리는 시간이라 둘이 같지 않다.
 */

/** 컨테이너가 일어서기를 기다리는 상한.
 *
 * ⛔ **2026-09-20 현재 이 30초는 근거가 무너진 채로 남아 있는 값이다.** 이 머리말은 두
 * 부분이다. 앞쪽 "CPU 시절 이력"은 **지금 근거가 아니고**, 뒤쪽 Modal 절이 지금 상대다.
 * 숫자만 보고 근거를 앞에서 가져다 쓰지 마라.
 *
 * ## ⬛ CPU 시절 이력 (2026-09-16. 지금 근거가 아니다)
 *
 * 당시 상대는 **Cloudflare CPU 컨테이너**(`standard-2`)였고, 통로는
 * `apps/api/src/lib/imaging-container.ts` 의 DO 였다. 기다린 것은 인스턴스 배치 + 이미지
 * 받기 + uvicorn 기동 + 모델 세션(2.5초 실측) + 해시 대조(0.2초)다.
 *
 * ### 실측 9.0초, 여유 배수 3 (45초 → 30초)
 *
 * 개발 워커 `makit-api-dev` 에서 콜드 스타트 **9.0초**를 쟀다. 이전 값 45초는
 * `apps/api/src/lib/imaging-container.ts` 의 `BOOT_DEADLINE_MS` 를 그대로 옮긴 것이었고,
 * 그 45초는 **재 본 적이 없어서 넉넉히 잡은 값**이었다. 이제 잴 수 있게 됐으므로 근거 없이
 * 큰 값을 들고 있을 이유가 없다. 지금 값은 9.0 × 3 = 27 을 **30 으로 올린 것**이다.
 * ⚠️ 그 9.0초는 **CPU 컨테이너에서 잰 관측 하나**다. 지금 값 30초가 여기서 나왔다.
 *
 * **왜 `/v1/compose` 왕복으로 잰 회차의 값을 여기에는 썼는가.** 기동 시간은 엔드포인트와
 * 무관하기 때문이다. 모델 세션은 요청 때가 아니라 **앱을 만들 때 한 번** 열렸다
 * (`services/imaging/src/imaging/app.py` 의 `create_app` 이 `load_engine()` 을 부른다).
 * 즉 합성으로 잰 콜드 스타트 안에 222MB 세션 열기와 해시 대조가 **이미 들어 있었다.**
 * ⛔ 왕복 쪽은 사정이 달랐다. `CUTOUT_PER_IMAGE_ESTIMATE_MS` 를 보라.
 *
 * **왜 3 배였나.** 9.0초는 **관측 한 번**이다. 이미지가 그 노드에 이미 받아져 있었는지,
 * 인스턴스 배치가 얼마나 늦어질 수 있는지의 **분산을 몰랐다.** 분산을 모를 때 상한을 관측값
 * 가까이 붙이면 정상 기동을 우리가 끊게 되고, 그 끊김의 이름은 `CONTAINER_NOT_READY`
 * (= `PROVIDER_FAILED`, 공급자 귀책)라 **우리 조급함이 공급자 실패율로 쌓인다.**
 *
 * ⚠️ 이 시간 동안 **셀러가 기다린다**(`/process` 가 요청 안에서 누끼를 돈다). 내린 15초는
 * 최악의 경우 셀러가 덜 붙잡히는 시간이라 값이 있었다.
 *
 * ### ⛔ 그 30초는 **CPU 시절 기준으로도 최소 5배 틀린 값이었다** (2026-09-19 실측)
 *
 * CPU 콜드 호출이 **233초를 쓰고도 실패**했다. 상한 30초가 그 경로의 실제 콜드 시간과
 * 자릿수로 갈려 있었다는 뜻이다. **관측 하나로 상한을 정한 대가**가 여기서 드러났다.
 * ⛔ **그렇다고 233초를 새 근거로 옮겨 적지 마라.** 지금 상대는 CPU 컨테이너가 아니라
 * Modal L4 GPU 이고, 233초는 이제 **무관한 숫자**다. 이 파일이 다른 상수마다 적어 둔
 * "남의 숫자를 이 자리에 들이지 마라"와 같은 규율이다.
 *
 * ## ⭐ 지금 상대는 **Modal L4 GPU** 다. 그리고 **콜드 스타트를 아무도 모른다** (2026-09-20)
 *
 * 누끼 공급자가 Cloudflare CPU 컨테이너에서 **Modal L4 GPU** 로 넘어갔다(PR #45 머지.
 * `services/imaging/modal_app.py`). 지금 이 상한이 덮는 것은 **BiRefNet 가중치를 L4 에
 * 올리는 시간**이고, **잰 적이 없다.**
 *
 * ⛔ **숫자를 지어내 이 자리에 적지 마라.** 지금 적을 수 있는 사실은 "모른다" 하나다.
 * 위 CPU 시절 숫자(9.0초 · 233초)도 지어내기와 같다. 상대가 다르다.
 *
 * ⚠️ **콜드는 드문 경로가 아니라 흔한 경로다.** `modal_app.py` 가 `min_containers=0` ·
 * `scaledown_window=30` 이라 **30초만 놀아도 컨테이너가 내려간다.** 셀러가 사진을 고르는
 * 동안 이미 식는다. 즉 이 상한은 예외를 위한 보험이 아니라 **보통 회차가 밟는 줄**이다.
 * 틀리면 드물게 틀리는 것이 아니라 **늘 틀린다.**
 *
 * ## 어떻게 재는가 (이 줄을 고칠 자격이 되는 유일한 관측)
 *
 * 컨테이너를 **내린 상태**(마지막 호출 뒤 `scaledown_window` 30초 경과)에서 `/v1/cutout` 을
 * 한 번 보내고, **바로 이어서 더운 상태로 한 번 더** 보낸다. **두 왕복의 차**를 콜드 몫으로
 * 본다.
 * ⛔ **Modal 대시보드의 함수 실행 시간과 우리 Worker 왕복은 다른 값이다. 둘을 섞지 마라.**
 * 대시보드 값에는 우리가 기다리는 구간의 일부가 빠져 있고, 그것을 이 상한에 넣으면 정상
 * 기동을 우리가 끊는 쪽으로 틀린다.
 *
 * ## 그래서 지금 30초를 유지하는 이유는 **하나뿐이다**
 *
 * **상한이 아예 없는 것보다는 낫다.** 그 밖에 이 값을 지지하는 근거는 지금 없다.
 * 위 재는 법의 실측이 나오는 날 **고칠 줄**이다. */
export const CUTOUT_COLD_START_BUDGET_MS = 30_000;

/** 장 하나를 처리하는 데 드는 시간 **추정치**.
 *
 * ⛔ **2026-09-16 의 왕복 0.55~0.76초는 이 값의 실측이 아니다. 이 값을 그쪽으로 내리지
 * 마라.** 그 회차가 잰 왕복은 **`/v1/compose`(합성)** 이고, 합성은 이미 만들어진 사진을 틀에
 * 얹어 다시 굽는 일이라 **모델 추론이 없다.** 이 상수가 덮는 것은 **`/v1/cutout`** 이고,
 * 그쪽은 222MB ONNX 를 1 vCPU(`standard-2`) CPU 위에서 돌린다. 두 일의 크기가 다르다.
 * 콜드 스타트는 같은 회차 값을 썼는데 여기는 못 쓰는 이유가 그것이다 —
 * 기동은 엔드포인트와 무관하고, **왕복은 엔드포인트 그 자체다.**
 *
 * ⚠️ **이 파일에서 가장 비싼 오해가 바로 이것이다.** 0.76초를 보고 장당 추정을 0.8초로
 * 내리면 예산이 `30초 + 장수 × 2.4초` 가 되고, **서버가 정상 추론을 스스로 끊는다.**
 * 끊긴 자리에는 원가만 나가고 결과가 없다(공급자 호출은 장당 과금이다).
 *
 * ⚠️ 지금 값 3초는 **Photoroom 상용 API 를 쓰던 시절 것이다.** 같은 숫자가 화면 안내
 * (`한 장에 3초쯤 걸립니다`)에도 있었는데 **2026-09-18 에 네 자리에서 전부 걷혔다**
 * (`A-24`·`A-24b`·`W-24`·`A-25`. 근거 PRD `7.6.7`·`7.6.8`). **잰 적이 없는 숫자를 셀러가
 * 기다릴 시간의 약속으로 적고 있었기 때문이다.** 지금 공급자 조합으로 장당 몇 초인지는
 * **여전히 잰 적이 없고**, 그래서 **이 상수가 그 값이 남은 마지막 자리다.**
 *
 * ⛔ **화면이 걷었다고 이 값을 따라 내리거나 비우지 마라.** 화면은 숫자를 말하지 않으면 그만
 * 이지만 **예산은 비울 수가 없다.** 근거가 약한 상한이라도 있어야 서버가 끝없이 매달리지
 * 않는다. **되돌릴 조건은 화면과 같은 실측 하나**이고 `7.13` **D-78** 이 두 자리를 함께
 * 닫는다. 다만 **고칠 주체가 다르다**: 이 상수는 `data-manager`, 화면 문구는
 * `design-manager` 다. 한쪽만 고치면 같은 숫자가 다시 한 자리에만 남는다.
 *
 * ⛔ 이 값이 틀리면 조용히 틀린다. 짧게 잡았으면 서버가 정상 처리를 스스로 끊고, 길게
 * 잡았으면 셀러가 이미 실패한 요청을 몇 분 더 본다. **남은 셋 중 가장 먼저 고칠 줄이다.**
 * 재는 법은 하나뿐이다: **이미 일어선 컨테이너**에 `/v1/cutout` 을 보내 왕복을 잰다.
 * 콜드 스타트가 섞인 숫자를 쓰면 이 자리에 또 남의 숫자가 들어온다. */
// 2026-09-19 교정: 위3초 설명은 폐기된 추정의 이력이다.
// BEN2 로컬 CPU 관측14~42초(wrangler.toml), 이번1thread 순수 추론25.66초/로드3.13초를
// 근거로 상단42초를 채택한다. 운영 전체 왕복 실측은 아직 없어 안전배수3은 유지한다.
export const CUTOUT_PER_IMAGE_ESTIMATE_MS = 42_000;

/** 위 추정이 틀렸을 때를 위한 배수. `apps/mobile` 의 `PROCESS_TIMEOUT_MARGIN` 을 옮긴 것이다.
 *
 * **추정치에만 곱하고 콜드 스타트에는 곱하지 않는다.** 콜드 스타트 상한은 이미 "여기까지만
 * 기다린다"는 상한이라 배수를 곱하면 상한이 상한이 아니게 된다. 불확실한 것은 장당 시간
 * 쪽이고, 그쪽이 장수만큼 곱해져 커진다.
 *
 * ⚠️ **콜드 스타트 상한도 자기 안에 3 배 여유를 품은 꼴이다.** 그 3 과 이 3 은 **다른
 * 숫자이고 서로 곱해지지 않는다.** 여기 값을 내린다고 콜드 스타트 상한이 따라 내려가지
 * 않고, 그 반대도 아니다.
 *
 * ⛔ **다만 그쪽 3 배는 무너진 관측 위에 서 있다.** 그 배수의 밑이 된 9.0초는 **CPU 컨테이너
 * 시절 관측**이고, 지금 상대(Modal L4 GPU)에서는 잰 적이 없다. 즉 **두 3 이 다른 숫자라는
 * 관계는 그대로 참인데, 그쪽 3 이 딛고 선 바닥은 없다.** 사정은
 * `CUTOUT_COLD_START_BUDGET_MS` 머리말에 있다. 여기 옮겨 적지 않는다.
 *
 * ⚠️ **장당 시간을 실측으로 바꾸면 이 배수도 같이 내릴 후보가 된다.** 3 은 "얼마나 틀렸는지
 * 모른다"는 뜻이지 관측된 분산이 아니다. 둘을 함께 보지 않고 장당 시간만 올리면 예산이
 * 세 배로 늘어난다. */
export const CUTOUT_BUDGET_SAFETY_FACTOR = 3;

/** 앱이 서버보다 **늦게** 끊게 하는 여유.
 *
 * 서버가 예산 끝에서 응답을 만들기 시작해도 그 응답이 앱에 닿기까지 시간이 든다(직렬화 +
 * 네트워크 왕복, 셀룰러면 더). 여유가 0 이면 **서버가 제때 실패를 적었는데도 앱은 그 답을
 * 못 받는다.** 앱이 받지 못한 실패는 화면에서 갇힘과 구분되지 않는다.
 *
 * ⚠️ 이 값만은 늘려도 셀러가 더 기다리지 않는다. 서버가 제 시각에 끝내면 답이 그 전에 오고,
 * 안 끝내는 경우에만 쓰인다. 그래도 5초를 넘기면 "실패인데 화면이 도는 시간"이 되므로
 * 무한정 늘리지 않는다. */
export const CUTOUT_CLIENT_SLACK_MS = 5_000;

/** 기존5분 처리 임대. API 잠금과 앱 멈춤 판정이 같은 값을 사용한다. */
export const CUTOUT_PROCESSING_LEASE_MS = 300_000;
/** 임대 만료와 정상 처리 종료가 겹치지 않도록 두는 보수적 정책 여유.
 * 실측 지연값이 아니다. D1/R2 예약과 별개로 회차를4분 안으로 제한한다. */
export const CUTOUT_LEASE_SLACK_MS = 60_000;
export const CUTOUT_MAX_SERVER_BUDGET_MS = CUTOUT_PROCESSING_LEASE_MS - CUTOUT_LEASE_SLACK_MS;

/** **장 하나를 처리하는 동안 공급자 밖에서 도는 왕복 수.**
 *
 * `CUTOUT_PER_IMAGE_ESTIMATE_MS` 가 덮는 것은 `/v1/cutout` 왕복 **하나**뿐이다. 그런데
 * `apps/api/src/routes/jobs.ts` 의 `processImage` 는 그 호출 앞뒤로 D1 과 R2 를 여러 번 더
 * 밟는다. 전부 `await` 라 **직렬**이고, 그 시간도 셀러가 기다리는 시간이다.
 *
 * ## 무엇을 세었나 (2026-09-18. 성공 경로를 한 줄씩 센 값)
 *
 * | # | 무엇 | 어디 |
 * |---|---|---|
 * | 1 | `ASSETS.head(outputKey)` | R2 |
 * | 2 | `markImageProcessing` | D1 |
 * | 3 | `ASSETS.get(sourceKey)` + `arrayBuffer()` | R2. **원본 바이트를 전부 읽는다** |
 * | 4 | `recordProviderCall` | D1 |
 * | 5 | `isLatestAttempt` | D1 |
 * | 6 | `ASSETS.put(outputKey, cut)` | R2. **누끼 PNG 를 전부 올린다** |
 * | 7 | `markImageDone` | D1 |
 * | 8 | `recordCall` 의 `recordBillingItem` | D1. `processImage` **밖**, 루프 안이다 |
 * | 9 | `makeRenditions` 의 `ASSETS.put(thumbKey)` | R2. 거의 안 탄다(~100KB). **2026-09-19** |
 * | 10 | `makeRenditions` 의 `ASSETS.put(previewKey)` | R2. 탄다(~1MB). **2026-09-19** |
 * | 11 | 합성본의 `ASSETS.put(composedKey)` | R2. 거의 안 탄다(860×860 PNG). **2026-09-19**. 2026-10-25 부터 키가 **불변**이다(`composed/detail/<배경>/<정책판>/<파일>`) |
 * | 12 | 합성본 **호환 거울**의 `ASSETS.put(legacyKey)` | R2. 거의 안 탄다(11 과 같은 바이트). **2026-10-25**. 옛 키(`composed/detail/<배경>/<파일>`)를 읽는 `routes/jobs.ts` 를 위한 것이고, 그 독자가 조회 도우미로 옮기면 걷는다(`docs/DATA_DICTIONARY.md` 25.8) |
 *
 * ⛔ **표 밖: `POST /v1/renditions` 와 `POST /v1/compose` 는 이 상수에 안 센다.** 9·10·11·12 와
 * 같은 커밋들에서 늘어난 `await` 지만 **상대가 저장소가 아니라 컨테이너 CPU** 라 왕복 하나의
 * 크기가 자릿수로 다르다. 그 몫은 `RENDITION_CALL_ESTIMATE_MS` 와
 * `COMPOSITION_CALL_ESTIMATE_MS` 가 따로 진다.
 * ⛔ **11 · 12 번은 "합성 왕복"이 아니다.** 여기 센 것은 **합성 결과를 R2 에 넣는 `put` 둘**(불변 키
 * 하나, 호환 거울 하나)뿐이고,
 * 컨테이너를 부르는 왕복은 위 문단대로 표 밖이다. 둘을 같은 줄로 읽으면 같은 시간을 두 번 뗀다.
 * ⛔ **그렇다고 여기에 "250ms 짜리 왕복 두 개"로 적어 시간을 맞추지 마라.** 이 상수가 세는
 * 것은 **코드에 있는 `await` 의 개수**다. 시간을 맞추려고 개수를 2 로 적으면 다음 사람이
 * 코드를 세어 보고 1 로 "고친다." 그 순간 예산이 조용히 줄고, 줄어든 예산은 갇힌 장으로
 * 돌아온다. ⭐ **개수는 개수로 세고, 시간이 다르면 상수를 가른다**
 * (`docs/DATA_DICTIONARY.md` 24.10 ⓐ).
 *
 * ⛔ **이 표를 지우고 숫자만 남기지 마라.** `processImage` 에 `await` 가 하나 늘었을 때 고칠
 * 자리가 여기라는 것을 말해 주는 것이 이 표뿐이다. 근거를 모르는 숫자는 아무도 못 건드리고,
 * 못 건드리는 숫자는 조용히 낡는다.
 *
 * ⚠️ **첫 장만 더 든다. 그래도 상수로 빼지 않았다.** `ensureJobPayment`(`lib/billing.ts`)가
 * 결제 행을 세우는데 **읽기 + 삽입 + 읽기라 D1 세 번**이다(`readJobPayment` → `INSERT ...
 * ON CONFLICT DO NOTHING` 의 `run()` → `readJobPayment`. 셋 다 `await` 라 직렬이다).
 * 살아 있는 결제가 이미 있으면 첫 읽기에서 나가 한 번이다. 즉 그 작업을 **처음** 돌리는
 * 회차의 첫 장은 12 가 아니라 **15** 이고, 살아 있는 결제가 이미 있는 회차의 첫 장은
 * **13** 이다. 둘째 장부터는 `createBillingWriter` 가 결제 id 를 손에 들어(`paymentId ??=`)
 * 표 그대로 12 다. 빼지 않은 이유는 **그 비용이 첫 장을 시작할지 정한 뒤에 나가서 어떤
 * 판단도 바꾸지 않기 때문**이다. 지나간 뒤에는 `CUTOUT_TAIL_ROUNDTRIPS` 의 ⓑ 가 말하는
 * 대로 시계가 이미 삼킨다.
 *
 * ⛔ **이 세 수(15 · 13 · 12)는 위 표에서 나온 파생값이다. 표가 한 줄 늘면 여기도 같이
 * 는다**(2026-10-25 에 호환 거울 put 이 붙어 14 · 12 · 11 에서 따라왔다). 2026-09-19 에 표가 8 → 10 → 11 로 두 번 늘었는데 **이 문단만 10 · 13 에 머물러
 * 있었고**(2026-09-20 이중 코드리뷰가 찾았다), 그 틀린 수가 "근거를 모르는 숫자는 아무도
 * 못 건드린다"고 적어 둔 바로 이 파일에 앉아 있었다. **근거를 적어 두는 것만으로는
 * 안 되고, 근거에서 나온 수까지 같은 커밋에서 따라와야 한다.**
 *
 * ⚠️ **열둘 중 셋(3 · 6 · 10)이 크기를 탄다.** 그 셋은 사진을 그대로 통과시켜서 같이
 * 커진다. 나머지 아홉은 사진이 커도 거의 그대로인데, **그 아홉이 한 갈래가 아니라 셋이다.**
 *
 * | 갈래 | 몇 · 어느 줄 | 왜 크기를 안 타나 |
 * |---|---|---|
 * | 문장 하나짜리 D1 왕복 | **다섯**(2 · 4 · 5 · 7 · 8) | 오가는 것이 행 하나지 바이트가 아니다 |
 * | 작은 객체 `put` | **셋**(9 · 11 · 12) | 썸네일 ~100KB, 합성본은 틀에 앉은 860 × 860(12 는 11 과 같은 바이트) |
 * | **본문 없는 R2 `head`** | **하나**(1) | ⭐ **`head` 는 메타데이터만 받고 본문을 안 받는다.** 그래서 9MB 를 가리키는 키여도 왕복 크기가 안 변한다 |
 *
 * ⭐ **1 번을 어느 갈래에도 안 넣은 채로 두 회차를 보냈다**(2026-09-20 이중 코드리뷰가 찾았다.
 * 표가 열이던 때에도 이미 하나가 비어 있었다). **합이 안 맞는 표는 다음 사람이 못 믿는다.**
 * 안 믿으면 못 건드리고, 못 건드리는 숫자는 조용히 낡는다. 위 ⛔ 가 말한 것과 같은 자리다.
 * **11 · 12 번이 작은 쪽인 것은 합성본이 틀에 앉은 860 × 860 이라 원본 해상도를 안 따라가기
 * 때문이다**(`docs/TEMPLATE.md` 2장 B). 크기를 타는 셋을 한 숫자로 뭉친 것이
 * `CUTOUT_ROUNDTRIP_ESTIMATE_MS` 이고, 뭉친 대가를 그쪽에 적어 뒀다.
 *
 * ⚠️ **`head` 가 싼 것은 왕복 크기뿐이다.** 왕복 **수**로는 다른 열과 똑같이 하나다. 싸다고
 * 이 상수에서 빼지 마라. 이 상수가 세는 것은 개수이고, 크기 차이는 위 표가 진다. */
// 2026-09-19: 8 → 10. `processImage` 의 성공 경로에 `makeRenditions` 의 R2 put 둘이 늘었다
// (`apps/api/src/lib/rendition.ts`). 실측으로 고친 줄이 아니라 **코드를 세어 고친 줄**이다.
// 2026-09-19: 10 → 11. 같은 경로에 **합성본 put 하나**가 더 늘었다(③ 합성·배치).
// 여기도 **코드를 세어 고친 줄**이다 — `/v1/compose` 왕복은 세지 않는다(위 표 밖 문단).
// 2026-10-25: 11 → 12. 합성본 키가 정책판을 품는 불변 키가 되면서, 옛 키를 읽는 `routes/jobs.ts` 를
// 위해 **호환 거울 put 하나**가 더 붙었다(12 번). 거울을 걷는 PR 이 이 줄을 11 로 되돌린다.
// ⛔ 합성본 메우기(`routes/compositions.ts`)는 `/process` 루프 밖의 요청이라 이 상수에 안 센다.
export const CUTOUT_ROUNDTRIPS_PER_IMAGE = 12;

/** **마지막 장이 끝난 뒤에도 남아 있는 왕복 수.** 예산에서 미리 떼어 둬야 하는 몫이다.
 *
 * ## ⓐ 뒤에 남은 것과 ⓑ 이미 지나간 것을 가른다. 안 가르면 같은 시간을 두 번 센다
 *
 * `/process` 한 요청에는 장수와 무관한 왕복이 루프 **양쪽**에 있다. 둘을 한 상수로 묶으면
 * 안 된다.
 *
 * **ⓑ 루프 앞은 여기 없다.** `getJobForSeller` · `claimJobForProcessing` ·
 * `reclaimStrandedImages` · `appendProcessingRecovered` · `listImages` 로 네다섯 번인데,
 * `startedAt` 이 **라우트 첫 줄**에서 찍혀서 루프가 시계를 볼 때 그 시간은 **이미 지나가
 * 있다**(그 라우트가 "예산 시계는 요청이 들어온 때부터 돈다"고 적어 둔 것이 이 뜻이다).
 * 여기 넣어 예산에서 또 떼면 **이미 쓴 시간을 한 번 더 빼는 것**이 되고, 서버는 그만큼 장을
 * 괜히 미룬다. 미룬 장은 셀러가 `만들기` 를 한 번 더 눌러야 하는 장이다.
 *
 * **ⓐ 루프 뒤만 여기 있다.** 아직 안 쓴 시간이라 떼어 두지 않으면 그대로 넘긴다.
 *
 * | # | 무엇 | 어디 |
 * |---|---|---|
 * | 1 | `finishJob` 의 `listImages` | D1 |
 * | 2 | `finishJob` 의 `releaseJobLock` | D1 |
 * | 3 | `finishJob` 의 `getJobForSeller` | D1 |
 * | 4 | `finishJob` 의 `loadJobView` | **D1 셋 + R2 `list()` 둘.** `Promise.all` 이라 기다리는 시간은 한 번 |
 * | 5 | `appendCutoutFinished` 의 `appendJobEvent` | D1 |
 *
 * ⚠️ **2026-09-19: 4 번의 횟수는 5 그대로인데 한 번의 길이가 길어졌다.** 축소본이 있는지를
 * R2 에 묻는 `listRenditions` 가 그 `Promise.all` 에 **네 번째**로 들어갔고, 합성본을 묻는
 * `list()` 가 **다섯 번째**로 들어갔다(`lib/rendition.ts` · 24.10 ⓒ · 25.5). 병렬이라
 * 기다리는 횟수는 늘지 않지만, 이제 그 한 번은 **D1 셋과 R2 둘 중 가장 느린 것**이고 그
 * 최댓값은 십중팔구 R2 쪽이다. **다음에 `CUTOUT_ROUNDTRIP_ESTIMATE_MS` 를 잴 때 이 항을
 * 따로 본다.**
 * ⛔ **R2 목록 둘을 하나로 합치겠다고 작업 접두 전체(`jobs/<jobId>/`)를 긁지 마라.** 원본과
 * 누끼까지 끌고 오고, 읽는 양이 다시 만들기 횟수만큼 늘어난다(24.5 가 같은 자리에서 막았다).
 *
 * ⛔ **이 다섯을 빼먹으면 루프 안에서 넘기는 것보다 나쁘다.** 여기서 넘긴 시간은
 * `releaseJobLock` 이 **아직 안 돈** 시간이고, 그때 앱이 끊으면 장만 갇히는 것이 아니라
 * **작업 잠금까지 임대가 끝날 때까지 매달린 채로 남는다.**
 *
 * ⚠️ 4 번은 실제 질의가 다섯이라 **장수가 늘면 읽어 오는 양이 는다.** 기다리는 횟수는 한
 * 번이므로 여기서는 1 로 센다. 그 가정이 깨지려면 `loadJobView` 의 `Promise.all` 이 직렬로
 * 풀려야 하고, 그러면 이 숫자는 5 가 아니라 **9** 다(축소본이 붙기 전에는 7, 그 뒤 8 이었다).
 * **그 가정이 깨지지 않게 하는 것이 `coding-agent` 소관이다.** */
export const CUTOUT_TAIL_ROUNDTRIPS = 5;

/** 위 왕복 **하나**에 걸리는 시간 **추정치**.
 *
 * ⛔ **잰 적이 없다.** `CUTOUT_PER_IMAGE_ESTIMATE_MS` 와 똑같은 성격이고 **그쪽보다 더 얇다.**
 * 그쪽은 적어도 옛 공급자(Photoroom)에서 나온 숫자인데, 이 값은 **어떤 관측에서도 오지
 * 않았다.** 이 파일에서 근거가 가장 약한 줄이다.
 *
 * ⛔ **2026-09-16 의 왕복 0.55~0.76초를 여기 옮기지 마라.** 그것은 워커에서 **컨테이너**로
 * 나간 왕복이고, 여기가 재는 것은 워커에서 **D1·R2** 로 나가는 왕복이다. 상대가 다르다.
 * 같은 회차의 콜드 스타트를 다른 상수에 쓴 것을 보고 이것도 된다고 읽지 마라.
 *
 * ## 근거가 있는 것과 없는 것을 갈라 둔다
 *
 * **있는 것 둘.** ① 왕복 **수**(12 와 5)는 코드를 센 값이다. ② **틀리는 방향의 값이 다르다.**
 * 이 값은 예산에서 떼는 몫이라 **크게 잡으면 장이 미뤄지고 작게 잡으면 서버가 자기 예산을
 * 넘긴다.** 미뤄진 장은 원가가 0 이고 `PENDING` 그대로라 다음 회차가 집어가며, 그때는
 * 컨테이너가 더워서 콜드 스타트 몫까지 빠진다. 넘긴 예산은 **갇힌 장**이고 그것이 이 파일이
 * 생긴 이유다. 값이 이렇게 다르면 **모를 때는 크게 잡는 것**이 답이다.
 *
 * **없는 것 하나.** `250` 이라는 크기 자체다. 이 값은 **문장 하나짜리 D1 왕복에는 너무 크고
 * 몇 MB 짜리 R2 올리기에는 너무 작다.** 알면서 하나로 뒀다. 지금 고치는 것은 크기가 아니라
 * **부대비용 몫이 예산 어디에도 없던 상태**이기 때문이다. 크기는 그다음 일이다.
 *
 * ## 크게 잡으라는 말에는 선이 있다
 *
 * 이 값이 커지면 **첫 장조차 시작하지 않는 자리**가 온다. 1장 요청에서 루프가 무엇이든
 * 시작하려면 대략 `(12 + 5) × 이 값 + RENDITION + COMPOSITION < 콜드 스타트 상한(30초)`
 * 이어야 하므로 **약 0.82초 미만이어야 한다**(`(30 - 5 - 11) / 17` = 0.8235). ⚠️ **이 선은 계속 앞으로 당겨지고 있다** — `(8 + 5)`
 * 에 컨테이너 몫이 없던 때는 2.3초, 축소가 붙어 `(10 + 5) + 1,500` 일 때는 1.9초였고,
 * 합성이 붙어 `+ 3,000` 일 때는 1.375초였다. 2026-09-20 에 축소가 5,000, 합성이 11,000 이
 * 되면서 **0.875초**가 됐고, 2026-10-25 에 합성본 호환 거울 put 이 왕복 하나를 더해 **약 0.82초**가
 * 됐다. 지금 값 250 은 아직 그 안쪽이지만 **여유가 3.3배뿐이다.**
 * **컨테이너 호출이 하나 더 붙거나 위 두 상수가 또 오르는 날 다시 당겨진다.** 그때 이 줄을
 * 같이 고친다.
 * 넘기면 셀러가 `만들기` 를 눌렀는데 **아무 일도 일어나지 않고 빈 응답만 온다.**
 * 원가도 결과도 없는 회차라 갇힘보다 알아채기 어렵다.
 *
 * ## 재는 법
 *
 * 실기기가 필요 없다. 워커 로그면 된다. `/process` 한 회차에서 `processImage` 안의 `await`
 * 앞뒤로 `Date.now()` 차를 찍어 **D1 다섯과 R2 일곱을 따로** 본다(R2 쪽은 크기가 제각각이라
 * `head` · `get` · `put` 셋을 또 갈라 본다). 따로 봐서 둘이 열 배쯤
 * 갈리면 이 상수를 둘로 가르는 것이 다음 일이고, 비슷하면 하나로 두고 값만 고친다.
 * ⛔ **콜드 스타트가 섞인 첫 장은 빼고 본다.** 그러지 않으면 이 자리에 또 남의 숫자가
 * 들어온다. */
export const CUTOUT_ROUNDTRIP_ESTIMATE_MS = 250;

/** **축소본을 만드는 한 호출(`POST /v1/renditions`)의 몫.** 예산이자 시한이다.
 *  (2026-09-19 신설. `docs/DATA_DICTIONARY.md` 24.10 ⓑ)
 *
 * 누끼 직후에 화면용 축소본 둘(`THUMB` 320px · `PREVIEW` 1280px)을 우리 컨테이너에서 만든다
 * (`apps/api/src/lib/rendition.ts` 의 `makeRenditions`). 그 왕복 하나의 몫이다.
 *
 * ## 왜 `CUTOUT_ROUNDTRIP_ESTIMATE_MS` 에 안 뭉쳤나
 *
 * 그 값(250)은 **문장 하나짜리 D1 왕복과 R2 올리기를 뭉친 값**이고, 그쪽 주석이 "문장 하나
 * 짜리 D1 왕복에는 너무 크고 몇 MB 짜리 R2 올리기에는 너무 작다. 알면서 하나로 뒀다"고 적어
 * 뒀다. 이 호출은 **그 둘 중 어느 쪽도 아니다** — 상대가 저장소가 아니라 **컨테이너 CPU**
 * 이고, 그쪽이 "따로 봐서 열 배쯤 갈리면 상수를 가르는 것이 다음 일"이라고 한 자리에 정확히
 * 해당한다(487 : 250 이 아니라, 전송까지 실은 실제 크기가 그렇다).
 *
 * ## 1,500 을 고른 근거 넷. ⭐ **이 파일에서 관측에 닿은 두 번째 줄이다**(첫째는 콜드 스타트)
 *
 * | 근거 | 값 |
 * |---|---|
 * | 실측 (개발 기계, 3024×3350, **두 판 합쳐**) | **487ms** |
 * | `docs/PRD.md` 7.5.14 의 **설계 상한** (더운 컨테이너, CPU 작업만) | **800ms** |
 * | 전송 | 누끼 9MB 를 올리고 두 판 ~1.1MB 를 받는다. **실측에 안 들어 있다** |
 * | 컨테이너 CPU | `standard-2` 1 vCPU. **개발 기계보다 느리다** |
 *
 * 실측의 약 3배, 설계 상한 + 전송의 약 2배다. ⚠️ **틀리는 방향이 다르다**는 이 파일의 원칙을
 * 그대로 따랐다 — 크게 잡으면 장이 미뤄지고(원가 0, `PENDING`, 다음 회차가 집어간다), 작게
 * 잡으면 서버가 제 예산을 넘겨 **장이 갇힌다.**
 *
 * ⚠️ **찬 기동 몫이 없다.** 이 호출이 쓰는 컨테이너는 **방금 그 안에서 누끼를 돌린 더운
 * 컨테이너**다. 그 전제가 깨지는 날(축소를 누끼와 다른 요청으로 떼는 날) 이 값은 틀린다.
 *
 * ## ⭐ 예산이자 시한이다. 같은 숫자를 두 곳에 쓴다
 *
 * `renditionCallDeadlineMs()` 가 이 값을 그대로 돌려준다. **누끼에는 못 하는 일인데 축소에는
 * 된다.** 누끼 호출을 시한으로 끊으면 **장당 과금이 이미 나간 뒤라 원가만 버린다**(그래서
 * `CUTOUT_PER_IMAGE_ESTIMATE_MS` 는 예산이지 시한이 아니다). 축소는 끊어도 잃는 것이
 * **컨테이너 CPU 몇 백 ms 뿐이고 장은 `DONE` 그대로**다.
 *
 * 그래서 닫히는 갈래가 하나 있다. ⚠️ **축소 실패는 장을 실패시키지 않으므로 이 몫은 성공
 * 경로에서만 드는 시간이 아니다 — 실패해도 그 시간은 이미 나갔다.** 시한을 예산과 **같은
 * 수**로 두면 실패 경로의 지출이 예산을 넘지 못한다. 떼어 둔 몫이 그대로 **상한**이 된다.
 *
 * ⛔ **호출부에서 이 숫자에 무엇을 더하거나 곱하지 마라.** 그 순간 고칠 자리가 다시 둘이 된다.
 * `apps/api/src/lib/rendition.ts` 가 들고 있던 임시 `RENDITION_BUDGET_MS`(10,000)가 바로 그
 * 둘째 자리였고, 이 상수가 서면서 지운다.
 * ⛔ **이 시한이 덮는 것은 `/v1/renditions` 왕복 하나뿐이다.** 그 뒤의 R2 put 둘은
 * `CUTOUT_ROUNDTRIPS_PER_IMAGE` 의 9·10 이 이미 지고 있다. 여기서 또 덮으면 같은 시간을
 * 두 번 뗀다.
 * ⛔ **`imaging-container.ts` 의 `RESPONSE_DEADLINE_MS` 로 재활용하지 마라.** 둘은 다른
 * 호출의 시한이고, 섞으면 42초짜리 추론에 1.5초 시한이 걸린다(24.10 ⓓ). */
// 2026-09-20 Modal 교정: 위 1,500ms는 Cloudflare CPU 전환 전 추정의 이력이다.
// 실기기 요청의 Modal /renditions total은 1.23초·1.46초, 재처리는 1.71초
// (execution 1.67초)였다. Worker의 업로드와 응답 JSON 수신까지 덮는 기존 시한은
// 정상 축소 실행만으로도 초과할 수 있다. 외부 왕복 여유를 포함해 5초로 올린다.
// 이는 소수 관측으로 정한 보수적 초기 상한이며 Worker 왕복 p95 실측값은 아니다.
// 예산과 시한을 함께 올려 장당 부대비용도 7.25초 → 10.75초로 예약한다.
// 기존 누끼 결과를 재사용하는 축소 복구도 이 값을 사용하되 콜드 스타트는 별도다.
export const RENDITION_CALL_ESTIMATE_MS = 5_000;

/** **축소 호출 하나에 허용하는 시간.** 값은 `RENDITION_CALL_ESTIMATE_MS` 와 **같은 수**다.
 *
 * 예산에서 뗀 몫이 곧 상한이 되게 하려는 것이다. 근거와 금지는 그 상수 머리말에 있다.
 * **상수를 직접 쓰지 않고 이 함수를 부르는 이유**는, 언젠가 시한과 예산이 갈려야 할 때
 * 고칠 자리를 여기 하나로 남겨 두기 위해서다. 그날이 오면 **왜 갈랐는지를 여기 적는다.**
 *
 * ⚠️ 호출부는 이 값을 **남은 시간과 `Math.min` 으로** 쓴다. 남은 시간이 이보다 적으면
 * 적은 쪽이 이겨야 한다 — 이 값은 상한이지 보장이 아니다. */
export function renditionCallDeadlineMs(): number {
  return RENDITION_CALL_ESTIMATE_MS;
}

/** **산출물을 굽는 한 호출(`POST /v1/compose`)의 몫.** 예산이자 시한이다.
 *  (2026-09-19 신설. `docs/DATA_DICTIONARY.md` 25장)
 *
 * 누끼 성공 직후에 그 장을 `docs/TEMPLATE.md` 의 틀에 앉혀 **860 × 860 PNG**(2장 B ·
 * 4장 상수)를 만든다. ⚠️ **여기 `· 그림자`가 함께 적혀 있었다. 2026-09-20 에 뗐다**
 * (두 산출물 모두 그림자를 두지 않는다. `docs/TEMPLATE.md` 4장). 0.1 의 **③ 합성·배치**
 * 이고, 그 문서가 **"여기서부터 산출물"**
 * 이라고 못박은 단계다. 그 왕복 하나의 몫이다.
 *
 * ## 왜 `RENDITION_CALL_ESTIMATE_MS` 에 안 뭉쳤나. 상대가 같은 컨테이너인데도 가른다
 *
 * **왕복 수를 개수로 세듯, 시간은 성격으로 가른다**(`CUTOUT_ROUNDTRIPS_PER_IMAGE` 의 ⭐).
 * 축소와 이 호출은 같은 컨테이너를 부르지만 **전송 꼴부터 다르다.** 축소는 multipart 로 원본
 * 바이트를 그대로 올리는데(`image` 파일 필드), 합성은 **base64 JSON 을 주고받는다.** 같은
 * 9MB 가 12MB 가 되어 올라가고 받는 것도 부풀어 온다. 한 숫자로 뭉치면 **둘 중 하나를 고칠
 * 때 다른 하나가 함께 움직이고, 그 움직임에는 근거가 없다.**
 *
 * ## 3,000 을 고른 당시 근거. **당시 축소 몫의 두 배였다**
 *
 * | 근거 | 값 |
 * |---|---|
 * | 당시 같은 컨테이너의 축소 몫 (`RENDITION_CALL_ESTIMATE_MS`) | **1,500** (Modal 전환 전 이력) |
 * | CPU 일의 크기 | ⚠️ **2026-09-20 에 한쪽 항이 사라졌다. 아래 ⭐ 를 먼저 본다** (그때 적은 것: ~~축소와 같은 급. 디코드는 똑같이 한 번, 인코드가 하나 적은 대신 **그림자 블러**가 붙는다~~) |
 * | 전송 | ⚠️ **축소보다 나쁘다.** base64 JSON 왕복이라 9MB 가 12MB 로 올라간다 |
 * | 워커 CPU | base64 인코딩이 **이 항 안에 있다.** 왕복이 아니라 왕복 수에는 안 세지만, **시간이 실릴 자리가 여기 말고 없다** |
 * | 컨테이너 CPU | `standard-2` 1 vCPU |
 *
 * 즉 **`축소와 같은 급의 CPU × 두 배로 나쁜 전송` = 1,500 × 2**. 관측이 아니라 **관측된 값
 * 하나(축소 487ms)에서 성격 차이만큼 밀어 올린 값**이고, 그 사실을 숨기지 않는다.
 *
 * ⚠️ **2026-09-16 의 왕복 0.55~0.76초가 바로 이 엔드포인트의 실측이다. 그런데 근거로 쓰지
 * 않았다.** 그 회차는 **개발용 합성 통로**(`/api/dev/composition/compose`)로 폰에서 보낸
 * 것이라 **입력이 누끼 PNG 9MB 였다는 근거가 없다.** 이 파일이 다른 상수마다 "남의 숫자를
 * 이 자리에 들이지 마라"라고 적어 둔 것과 같은 규율이다 — **엔드포인트가 맞는다고 입력까지
 * 맞는 것은 아니다.** ⭐ **하한으로만 쓴다**: 3,000 은 그 관측의 약 4배이고, 관측이 작은 입력
 * 이었다면 그 배수는 실제로 더 작다.
 *
 * ## ⭐ 2026-09-19. **실측이 들어왔고 3,000 이 그대로 섰다. 값은 안 고친다**
 *
 * 위 두 문단은 추정이었다. `coding-agent` 가 **진짜 누끼를 진짜 서비스에 왕복**시켜 재 왔다
 * (개발 R2 의 9.03MB · 8.70MB 두 장. 이 저장소의 `lib/composition.ts` 를 그대로 돌리고 R2
 * 자리에만 디스크를 끼웠다).
 *
 * | 무엇 | 값 |
 * |---|---|
 * | 왕복 (base64 인코딩 + HTTP + 합성 + 디코드 **전부 포함**) | **1,084ms · 973ms** |
 * | 산출물 | 860 × 860 PNG, 324KB · 322KB (`composition-policy.v1`) |
 * | 3,000 은 이 값의 | **약 2.8배** |
 *
 * ⭐ **이 실측이 위 추정을 뒤집지 않고 같은 자리에 떨어졌다.** 옆 상수
 * (`RENDITION_CALL_ESTIMATE_MS` = 1,500)도 실측 487ms 의 **약 3배**다. 두 상수가 이제 **같은
 * 종류의 근거 위에 같은 배수로** 서 있었다. 이 비교는 Modal 전환 전의 이력이며, 현재
 * 축소 몫은 5,000ms여서 같은 배수가 아니다. 당시 추론(전송 차이만큼 올린다)은 남긴다.
 *
 * ⚠️ **개발 기계의 숫자다. 컨테이너는 `standard-2` 1 vCPU 라 더 느리다.** 487ms 도 같은
 * 성격의 숫자라 배수 비교는 성립하지만, **둘 다 실제 런타임보다 낙관적이다.** 3,000 을
 * 이 실측에 맞춰 내리면 그 낙관을 상한으로 굳히는 것이 된다.
 *
 * ⛔ **그래서 재고도 안 내린다. 이유는 시간이 아니라 회복 경로다.** 끊긴 합성을 다시
 * 집어가는 자리가 아직 비어 있어서(아래 ⚠️ 와 `docs/DATA_DICTIONARY.md` 25.3), **시한을
 * 짧게 잡는 것이 곧 그 장의 산출물을 영영 안 만드는 것**이다. 내릴 수 있게 되는 조건은
 * "더 잘 쟀다"가 아니라 **"못 만든 장이 되돌아온다"** 다.
 *
 * ⭐ **위 1,084ms 는 인코딩까지 포함한 벽시계라, 아래 시한이 덮는 구간은 그보다 짧다.**
 * 즉 이 실측은 시한 쪽에서는 **상한**으로 읽어야 한다. 9MB 인코딩이 그중 얼마인지는 아직
 * 안 갈랐고, 그것을 가르기 전에는 아래 ⚠️ 의 "헐거움"이 얼마인지도 모른다.
 *
 * ## ⭐ 2026-09-20. **그림자가 걷혀 위 표의 CPU 항이 무너졌다. 값은 그대로다**
 *
 * `7490d88` 이 그림자 단계를 지웠다(두 산출물 모두 두지 않는다. `docs/TEMPLATE.md` 4장).
 * 위 표의 CPU 줄은 **"인코드가 하나 적은 대신 그림자 블러가 붙는다"는 상계**였는데 그
 * 한쪽이 없어져서, 양쪽 항을 개발 기계에서 **CPU 단계만 떼어** 쟀다.
 *
 * | 잰 것 | 중앙값 |
 * |---|---|
 * | 걷어낸 그림자 단계 (알파 투영 + 24px 가우시안 + 캔버스 한 층 합성. 31회) | **6.7ms** |
 * | 그 상계의 반대편, 860 × 860 PNG 인코드 한 번 (9회) | **44.8ms** |
 *
 * ⭐ **상계가 애초에 대등하지 않았다.** 그림자가 있던 때에도 합성의 CPU 는 축소보다
 * 가벼웠고, 걷힌 지금은 더 벌어졌다. **"축소와 같은 급"은 등식이 아니라 상한으로 읽는다.**
 * ⚠️ 그림자 항은 피사체를 상자에 맞춰 줄인 **뒤에** 돌던 단계라 입력 사진 크기에 안 딸린다.
 *
 * ⛔ **그래도 3,000 은 안 고친다.** ① CPU 항이 가벼워지는 것은 예산이 헐거워지는 쪽이라
 * 이 파일의 "틀리는 방향이 다르다" 원칙이 그대로 산다. ② 위 실측 1,084ms 는 **그림자가
 * 있던 때의 값**이라 지금 코드에는 상한이다. ③ 내리지 않는 진짜 이유가 여전히 CPU 가
 * 아니라 회복 경로다(아래 ⚠️ 와 25.3). ⚠️ **그림자 없는 왕복 전체는 다시 재지 않았다.**
 * 자세한 것은 `docs/DATA_DICTIONARY.md` 25.2 의 같은 날 기록에 있다.
 *
 * ## ⭐ 예산이자 시한이다. 같은 숫자를 두 곳에 쓴다 — **다만 축소보다 근거가 약하다**
 *
 * `compositionCallDeadlineMs()` 가 이 값을 그대로 돌려준다. 축소와 같은 논리다: 끊어도
 * **장당 과금이 안 나가고 장은 `DONE` 그대로**라 잃는 것이 컨테이너 CPU 뿐이다. 실패해도
 * 그 시간은 이미 나갔으므로, **시한을 예산과 같은 수로 둬야 실패 경로의 지출이 예산을 넘지
 * 못한다.**
 *
 * ⚠️ **그런데 "잃는 것이 CPU 뿐"이 축소만큼 참이 아니다.** 축소가 끊기면 화면이 **그림을 틀린
 * 크기로 그리는 것**으로 끝나지만, 합성이 끊기면 **그 장에 산출물이 아예 없다**(폴백을 만들지
 * 않았다. ② 를 산출물이라고 부르지 않기 때문이다). ⛔ **그래서 이 값을 내릴 때는 축소와 같은
 * 근거를 대면 안 된다.** 내리려면 **합성만 실패한 장을 무엇이 다시 집어가는지**가 먼저 있어야
 * 한다(25.3 의 미결). 지금은 그 자리가 비어 있어서 **끊긴 합성이 되돌아오지 않는다.**
 *
 * ⛔ **호출부에서 이 숫자에 무엇을 더하거나 곱하지 마라.** 그 순간 고칠 자리가 다시 둘이 된다.
 * ⛔ **이 시한이 덮는 것은 `/v1/compose` 왕복 하나뿐이다.** 그 뒤의 R2 put 둘은
 * `CUTOUT_ROUNDTRIPS_PER_IMAGE` 의 11 · 12 가 이미 지고 있다.
 * ⛔ **`renditionCallDeadlineMs()` 와 서로 바꿔 쓰지 마라.** 같은 컨테이너의 다른 호출이고,
 * 섞으면 위 표의 전송 차이가 그대로 사라진다. `imaging-container.ts` 의
 * `RESPONSE_DEADLINE_MS` 로 재활용하지 말라는 금지도 그대로다 — 그쪽은 42초짜리 추론이다.
 *
 * ## ⭐ 2026-09-20. **3,000 이 실기기에서 깨졌다. 11,000 으로 올린다**
 *
 * 위 근거는 **전부 Cloudflare CPU 컨테이너 시절의 것**이다. 공급자가 Modal 로 바뀐 뒤
 * **합성을 진짜 입력으로 다시 잰 적이 없었다.** 축소는 다시 재서 1,500 → 5,000 으로 올라갔는데
 * (`b7d281c`) **합성만 안 따라왔고**, 그 사이에 셀러가 물렸다.
 *
 * | 언제 (KST. 캡처의 로컬 시각) | 작업 | 장 | 그 장이 회차에서 몇째였나 |
 * |---|---|---|---|
 * | 2026-09-20 08:03 | `25796a70` | `c281a1b5` | **첫째**(1장 회차) |
 * | 2026-09-20 08:20 | `9ceed896` | `58f1a76a` | **첫째** |
 * | 2026-09-20 17:17 (= 08:17 UTC) | `9a780f9d` | `6f154da5` | 셋째(5장 회차) |
 *
 * 셋 다 `composition failed ... reason: 'DeadlineExceeded(3000ms)'` 다.
 *
 * ⭐ **괄호 안의 수가 이 기록에서 가장 중요하다.** 그것은 `withDeadline` 이 **실제로 기다린
 * 벽시계**(`apps/api/src/lib/deadline.ts` 의 `waitedMs`)이고, 셋 다 **3,000 을 온전히
 * 받았다**는 뜻이다. 회차 잔여가 모자라 잘린 것이 아니다. 앞의 둘은 **그 회차의 첫 장**이라
 * 예산이 가장 헐거운 조건이었다. 즉 **Modal 왕복 자체가 3초를 넘는다.**
 *
 * ⛔ **얼마나 넘는지는 모른다. 그 사실을 값에 담아야 한다.** 시한이 먼저 끊어서 **실제 소요를
 * 한 번도 못 봤다.** `DeadlineExceeded(NNNNms)` 의 `NNNN` 은 우리가 준 몫이지 걸린 시간이
 * 아니다. 성공한 왕복의 ms 는 지금 **아무 데도 안 남는다**(`composition done` 줄에 시간 칸이
 * 없고, `/v1/compose` 는 성공 로그가 없다. `verification/cases/pr-44.json` 이 같은 것을
 * 지적했다). **그래서 아래는 실측이 아니라 두 경로로 민 추정이고, 그 사실을 숨기지 않는다.**
 *
 * ### 11,000 을 어떻게 얻었나. 경로 둘이 같은 자리에 떨어졌다
 *
 * **경로 ①. 관측된 배수를 Modal 축소에 옮긴다.**
 *
 * | 단계 | 값 | 출처 |
 * |---|---|---|
 * | Modal 축소 관측 최댓값 | **1,710ms** | 실기기 1.23 · 1.46 · 1.71초 |
 * | 합성 ÷ 축소 (**같은 9MB 누끼로 잰 유일한 비**) | **2.23배** | CPU 시절 1,084ms ÷ 487ms |
 * | ⇒ Modal 합성 최댓값 **추정** | **3,813ms** | 1,710 × 2.23 |
 * | 축소가 관측 최댓값에 쓴 여유 배수 | **2.92배** | 5,000 ÷ 1,710 |
 * | ⇒ 같은 어법으로 민 상한 | **11,134ms** | 3,813 × 2.92 |
 *
 * **경로 ②. 상수끼리의 비를 지킨다.** 같은 입력으로 잰 비가 2.23 이므로
 * `5,000 × 2.23 = 11,150`.
 *
 * 둘 다 11,000 대에 떨어져 **11,000** 으로 잡는다. ⭐ **추정 3,813ms 가 깨진 3,000 보다 크다는
 * 것이 이 계산의 유일한 검증**이고, 그것은 관측과 어긋나지 않는다. ⛔ 반대로 **이 3,813 을
 * "잰 값"으로 옮겨 적지 마라.** 관측된 비 하나와 관측된 최댓값 하나를 곱한 수다.
 *
 * ### 왜 축소와 **같은 수**로 두지 않았나
 *
 * 우리가 가진 모든 숫자가 **합성이 축소의 2.2배**라고 말한다(CPU 시절 1,084 대 487). 5,000 은
 * 이미 깨진 3,000 의 1.67배일 뿐이고 추정 최댓값 3,813 의 **1.3배**라, 상한이 아니라 추정치
 * 옆에 붙어 앉는 수가 된다. **그리고 틀렸을 때 잃는 것이 두 호출에서 다르다**(위 ⚠️ 와
 * `docs/DATA_DICTIONARY.md` 25.3): 축소가 끊기면 화면이 다른 크기로 떨어지고, 합성이 끊기면
 * **그 장에 산출물이 아예 없고 아무도 다시 집어가지 않는다.** 두 상수를 가른 이유가 바로
 * 이것인데 값을 같게 두면 그 구분이 숫자에서 사라진다.
 *
 * ### ⛔ 올린 대가. **한 회차가 시작하는 장이 준다**
 *
 * `cutoutPerImageOverheadMs()` 가 **10,750 → 18,750ms** 가 되고, 루프가 새 장을 시작할 수 있는
 * 창(`요청 시작부터 잰 경과`)이 그만큼 줄어든다. 2장 이상 회차에서
 * `240,000 - 1,250 - 126,000 - 장당 부대비용` 이므로 **102,000ms → 94,000ms** 다.
 * (2026-10-25 합성본 호환 거울 put 으로 장당 부대비용이 19,000ms 가 되어 지금은 **93,750ms** 다.
 * 아래 표의 장수는 그대로다. 1장 회차 창은 10,000 → 9,750ms.)
 *
 * | 장 하나에 실제로 드는 시간 | 전(3,000) | 후(11,000) |
 * |---|---|---|
 * | 20초 (관측: 5장 회차가 60초에 세 장, 2장 회차가 약 49초에 두 장) | 6장 | **5장** |
 * | 25초 | 5장 | **4장** |
 * | 30초 | 4장 | 4장 |
 *
 * ✅ **1장 회차는 안 줄어든다.** 그쪽 창은 `156,000 - 1,250 - 126,000 - 부대비용` 이라
 * 18,000 → 10,000 으로 줄 뿐 여전히 0 이상이다. 되살리기·다시 만들기가 막히지 않는다.
 *
 * ⚠️ **미룬 장은 원가 0 이고 `PENDING` 이라 다음 `만들기` 가 집어간다.** 못 만든 산출물과
 * 바꾼 것이고, 그 교환이 맞는 방향이다. 다만 미뤘다는 사실이 셀러 화면에 드러나야 대가를
 * 치른 것이 된다(`cutoutOverheadMs` 의 ⓑ 대가 문단. 아직 `console.warn` 하나뿐이다).
 *
 * ⚠️ **오늘 저 대가가 실제로 나타나지 않을 수도 있다. 좋은 뜻이 아니다.** 같은 회차가
 * `waitUntil` 한계로 60초 언저리에서 잘리고 있어서(`docs/DATA_DICTIONARY.md` 29장) **예산이
 * 약속한 238.75초를 끝까지 써 본 적이 없다.** 예산을 쥐어짜기 전에 런타임이 먼저 끊는다.
 *
 * ### 되돌리거나 다시 내리는 조건
 *
 * ⛔ **"3초도 가끔 됐다"는 근거가 아니다.** 내릴 수 있는 조건은 둘 다 필요하다.
 * ① **성공한 왕복의 ms 가 남아야 한다**(`composition done` 에 `tookMs` 를 더하거나
 * `/v1/compose` 가 `total_ms` 를 찍는다. `coding-agent` 소관). ② 25.3 의 회복 경로가 서야
 * 한다. 그 전까지 시한을 줄이는 것은 **그 장의 산출물을 영영 안 만드는 것**이다. */
// 2026-09-20 Modal 교정: 위 3,000ms 는 Cloudflare CPU 컨테이너 시절 추정의 이력이다.
// 실기기에서 `DeadlineExceeded(3000ms)` 가 세 번 났고 그중 둘은 회차의 첫 장이었다.
// 실제 소요는 시한에 잘려 못 봤으므로 이 값은 실측이 아니라 두 경로로 민 보수적 상한이다.
export const COMPOSITION_CALL_ESTIMATE_MS = 11_000;

/** **합성 호출 하나에 허용하는 시간.** 값은 `COMPOSITION_CALL_ESTIMATE_MS` 와 **같은 수**다.
 *
 * 예산에서 뗀 몫이 곧 상한이 되게 하려는 것이다. 근거와 금지는 그 상수 머리말에 있다.
 * **상수를 직접 쓰지 않고 이 함수를 부르는 이유**는 `renditionCallDeadlineMs` 와 같다 —
 * 언젠가 시한과 예산이 갈려야 할 때 고칠 자리를 여기 하나로 남겨 두기 위해서다.
 *
 * ⚠️ 호출부는 이 값을 **남은 시간과 `Math.min` 으로** 쓴다. 이 값은 상한이지 보장이 아니다.
 *
 * ⚠️ **base64 인코딩 시간은 이 시한 밖이다.** 시한은 `fetch` 를 감싸는데 워커의 인코딩은 그
 * 앞에서 끝나 있다. **예산 쪽(위 상수)은 그 시간을 덮지만 시한은 못 덮는다** — 즉 "떼어 둔
 * 몫이 그대로 상한"이라는 문장이 여기서는 **인코딩 시간만큼 헐겁다.** 9MB 를 인코딩하는
 * 시간이 눈에 띄면 그때 두 값을 가른다. */
export function compositionCallDeadlineMs(): number {
  return COMPOSITION_CALL_ESTIMATE_MS;
}

/** 장수를 예산 계산에 쓸 수 있는 꼴로 만든다.
 *
 * ⛔ **`NaN` 을 그대로 통과시키지 않는다.** `Math.max(0, NaN)` 은 `NaN` 이고, `NaN` 이
 * `setTimeout` 에 들어가면 **0 으로 취급되어 요청이 즉시 취소된다.** 그러면 이 파일이 막으려던
 * 갇힘이 가장 빠른 형태로 일어난다. 음수 방어보다 이쪽이 더 위험한 갈래다. */
function normalizeImageCount(imageCount: number): number {
  if (!Number.isFinite(imageCount)) return 0;
  return Math.max(0, Math.trunc(imageCount));
}

/** **서버가 스스로 끊는 시각까지의 시간.** 이 제품의 시간 예산 정본이다.
 *
 * `콜드 스타트 상한 + 장수 × 장당 추정 × 안전 배수`.
 *
 * 지금 값으로(2026-09-16 콜드 스타트 실측 반영): 1장 **39초**, 권장 8장
 * (`RECOMMENDED_IMAGE_COUNT`) **102초**. 앱 상한은 여기에 여유 5초를 더해 44초 · 107초다.
 *
 * ⚠️ **1장 39초가 사고 때의 39초와 같은 숫자지만 다른 것이다.** 그때 39초는 **앱이 끊던**
 * 시각이었고 서버는 45초를 기다렸다(부등식이 뒤집혀 있었다). 지금 39초는 **서버가 끊는**
 * 시각이고 앱은 44초에 끊는다. 숫자가 같다고 예전 표를 그대로 옮겨 적지 마라.
 *
 * ⚠️ **임대(`PROCESSING_LEASE_SECONDS`, 5분)를 넘기면 안 된다.** 넘기는 순간 **아직 돌고 있는
 * 처리를 다른 요청이 죽은 것으로 보고 회수해 간다**(19장 `reclaimStrandedImages`). 그것은
 * 갇힘보다 나쁜 갈래다. 공급자 호출이 장당 과금이라 **같은 장에 원가가 두 번 나간다.**
 * 이 함수의 값만 보면 **30장에서 임대와 정확히 같아지고**(30 + 30 × 9 = 300초) **31장에서
 * 넘는다**(309초 > 300초).
 *
 * ## 그 선을 실제로 긋는 것은 이 함수가 아니라 **서버가 끝나는 시각**이다 (2026-09-18)
 *
 * 임대와 겨루는 것은 예산 숫자가 아니라 **서버가 마지막 문장을 쓰는 시각**이다. 부대비용을
 * 세기 전까지 그 둘이 같다고 여겼는데 **아니었다.**
 *
 * | 언제 | 서버가 실제로 끝나는 시각 | 임대(300초) 안쪽인 마지막 장수 |
 * |---|---|---|
 * | 부대비용을 안 세던 때 | `예산 + 장 하나 부대비용 + 루프 뒤 몫` = `예산 + 3.25초` | **29장**(294.25초) |
 * | `cutoutOverheadMs` 를 떼는 지금 | `예산 - 루프 뒤 몫` = `예산 - 1.25초` | **30장**(298.75초) |
 *
 * ⭐ **그래서 이 수정은 선을 당기지 않고 오히려 한 장 되돌려 놓는다.** 부대비용이 장수에
 * 비례하니 선이 앞으로 당겨질 것 같지만 그렇지 않다. **비례하는 몫은 루프가 `Date.now()` 를
 * 다시 보면서 이미 정산하고 있었고**(그만큼 장이 미뤄진다) 정산되지 않던 것은 마지막 장
 * 하나와 루프 뒤 몫뿐이었다(`cutoutPerImageOverheadMs`). 예산에서 그 둘을 떼면 서버는
 * 예산보다 **일찍** 끝나므로, **머리말이 적어 둔 30장/31장이 이제 참이 된다.** 그전에는
 * 참이 아니었다.
 *
 * ⚠️ **되돌아온 30장도 안전한 자리가 아니다.** 여유가 1.25초뿐이라 마지막 장의 공급자 호출이
 * 장당 추정을 조금만 넘겨도 회수와 처리가 같은 초에 겹친다. 그리고 이 표의 숫자는
 * `CUTOUT_ROUNDTRIP_ESTIMATE_MS`(잰 적 없음)에 기대고 있어, **그 값이 바뀌면 두 줄 다
 * 바뀐다.** ⭐ **장수 상한은 2026-09-23 에 닫혔다**(`upload.ts` 의 `MAX_IMAGES_PER_JOB` = 15.
 * `RECOMMENDED_IMAGE_COUNT`(8)는 예나 지금이나 권장이지 상한이 아니다). 임대 숫자는 아직
 * `apps/api/src/routes/jobs.ts` 에 산다.
 * **위 숫자 일곱 줄을 올릴 때 이 선을 함께 본다.**
 *
 * ⚠️ **상한 15 가 이 함수의 입력 상한은 아니다.** 이 함수가 받는 것은 **이번 회차가 집은
 * 장 수**이지 작업에 담긴 장 수가 아니다. 15장짜리 작업도 회차마다 몇 장씩 나뉘어 들어온다
 * (그 사정을 말하는 화면이 `A-24e` 다). ⛔ **`cutoutServerBudgetMs(15)` 를 어딘가에 미리
 * 계산해 "15장이면 몇 초"로 적지 마라.** 그 숫자는 아무 회차도 겪지 않는다.
 *
 * ⭐ 다만 **상한이 임대 산수를 한 방향으로 묶어 준다.** 위 표의 30장/31장 경계는 한 회차가
 * 30장을 집을 수 있다는 전제에서 나온 것인데, 이제 **작업 하나가 15장을 못 넘으므로 한
 * 회차가 집을 수 있는 장도 15장을 못 넘는다.** 경계까지 남은 거리가 그만큼 생겼다.
 * ⛔ 그렇다고 예산을 올릴 근거는 못 된다. 29장의 미결(`waitUntil` 실제 시간)은 그대로다. */
// 2026-09-19: 위39초/102초 및30장 임대 경계 계산은 이전3초 추정의 이력이다.
// 현재 장당 몫126초, 서버1장156초/2장 이상240초, 앱은161초/245초다.
// 장수 제한이 아니라 회차 상한이다. 남은 예산으로 시작할 수 없는 장은 PENDING 유지.
// count0=30초/count1=156초에는 cap이 걸리지 않아 두 호출부의 차분126초를 보존한다.
// 2026-09-19 축소본: 위 표의 "예산 - 1.25초"는 그대로다(루프 뒤 몫은 안 늘었다). 대신
// 장 하나 부대비용이 2.0초에서 4.0초가 되어, 한 회차가 시작하는 장이 그만큼 줄었다.
// 임대 경계(30장/31장) 산수는 이미 42초 추정에서 무효가 됐고 여기서 다시 계산하지 않는다.
// 2026-09-19 합성: 같은 자리에 또 붙었다. "예산 - 1.25초"는 여전히 그대로이고, 장 하나
// 부대비용만 4.0초 → 7.25초다. **이 함수는 한 글자도 안 바뀐다** — 부대비용은 예산 안이
// 아니라 `cutoutOverheadMs` 가 지고, 그 둘을 가른 이유가 아래 ⛔ 문단이다.
export function cutoutServerBudgetMs(imageCount: number): number {
  const count = normalizeImageCount(imageCount);
  return Math.min(
    CUTOUT_MAX_SERVER_BUDGET_MS,
    CUTOUT_COLD_START_BUDGET_MS +
      count * CUTOUT_PER_IMAGE_ESTIMATE_MS * CUTOUT_BUDGET_SAFETY_FACTOR,
  );
}

/** **공급자 밖에서 도는 부대비용의 시간 몫.** `cutoutServerBudgetMs` 에서 **떼어** 쓴다.
 *
 * `(루프 뒤 몫 + 장수 × 장당 몫) × 왕복 추정` **+ 장수 × (축소 호출 몫 + 합성 호출 몫)**.
 *
 * 지금 값으로: `cutoutOverheadMs(0)` = **1.25초**(루프 뒤 몫), 장 하나 몫 = **19초**
 * (`cutoutPerImageOverheadMs`. `12 × 250 + 5,000 + 11,000`), 8장 몫은 **153.25초**다
 * (`(5 + 8 × 12) × 250 + 8 × 16,000`). 2026-10-25 합성본 호환 거울 put 전에는 18.75초 · 151.25초였다.
 * 아래 7.25초·58초는 이전 CPU 예산의 이력이다. ⚠️ 그 사이에 축소만 5,000 이던 판의 값을
 * "8장 86초"로 적어 둔 적이 있는데 **그 판의 바른 값은 87.25초**였다(`93 × 250 + 8 × 8,000`).
 * 파생값은 상수와 같은 커밋에서 다시 계산한다.
 *
 * ⚠️ **2026-09-19: 항이 셋이 됐다.** 왕복 수에 곱하는 항 하나로 끝나지 않는다. 축소
 * (`POST /v1/renditions`)와 합성(`POST /v1/compose`)은 상대가 저장소가 아니라 컨테이너 CPU 라
 * 왕복 추정에 안 실리고, **장수에만 비례하는 별도 항**으로 각각 붙는다. ⛔ **루프 뒤 몫에는
 * 안 붙는다** — 둘 다 장마다 도는 일이고 루프가 끝난 뒤에는 돌지 않는다. 그래서
 * `cutoutOverheadMs(0)` 은 1.25초 그대로다.
 *
 * ⚠️ **장당 몫이 2.0초 → 4.0초 → 7.25초 → 10.75초 → 18.75초 → 19초로 왔다. 8장이면 153초가
 * 예산에서 빠진다**(직전엔 151초, 그전엔 87초, 그전에는 58 · 32 · 16초). **한 회차에 시작하는 장이 줄고
 * 미루는 장이 는다.** ⚠️ **2026-09-20 의 마지막 한 걸음(10.75 → 18.75)은 처음으로 그 대가가
 * 산수에서 보이는 걸음이다.** 2장 이상 회차의 시작 창이 102초에서 94초로 줄어, 장당 20초면
 * 여섯 장이 아니라 다섯 장을 시작한다(`COMPOSITION_CALL_ESTIMATE_MS` 의 대가 표). 그것이 맞는
 * 방향이지만(아래 ⓑ 갈림), 미룬 장이 셀러 화면에 드러나야 대가를 치른 것이 된다. 그 자리는
 * 아직 `console.warn` 하나뿐이다.
 *
 * ⛔ **"이 증가는 시작하는 장 수를 거의 안 바꾼다"는 문장이 여기 있었다. 2026-09-20 에
 * 만료됐다.** 그때의 논거는 **공급자 몫이 126초**
 * (`cutoutServerBudgetMs(1) - cutoutServerBudgetMs(0)`)라 그 옆에서 4.0 과 7.25 의 차이가
 * 경계에 걸리지 않는다는 것이었다. 맞는 말이었지만 **그 말은 "부대비용이 작을 때"에만
 * 참이었다.** 2장 이상 회차는 예산이 240초에 걸려 있어 부대비용이 커질수록 시작 창이 그대로
 * 깎이고, 18.75초에서는 장당 20초 기준으로 한 장이 실제로 준다. ⭐ **그 문장이 스스로 달아
 * 뒀던 경고가 맞았다**: "지금 싼 것이지 영원히 싼 것이 아니다." 공급자 몫이 실측으로
 * 내려가는 날(D-78) 이 항의 비중은 또 커진다.
 *
 * ## 갈림 둘 중 ⓑ 를 골랐다 (2026-09-18)
 *
 * **ⓐ `CUTOUT_CLIENT_SLACK_MS` 를 장수 비례로 만든다** / **ⓑ 예산에서 먼저 뗀다.** ⓑ 다.
 *
 * ① **여유의 뜻이 하나여야 한다.** `CUTOUT_CLIENT_SLACK_MS` 가 덮는 것은 **서버와 앱 사이**
 *    (직렬화 + 네트워크) 하나다. 서버 **안쪽**의 D1·R2 왕복을 그 칸에 얹으면 한 숫자가 성격이
 *    다른 둘을 담고, 그러면 다음 사람이 그 값을 어느 근거로 움직여야 하는지 알 수 없다.
 * ② **ⓐ 로는 이 파일이 절반만 참인 상태가 안 끝난다.** 머리말이 못 박은 문장이 있다.
 *    "서버가 이 예산에서 실제로 끊지 않으면 이 파일은 절반만 참이다." ⓐ 는 **앱을 더 기다리게
 *    할 뿐 서버는 여전히 자기 예산을 넘겨 돈다.** ⓑ 는 `cutoutServerBudgetMs` 를 **"서버가
 *    진짜 그 안에 끝낸다"** 는 말로 되돌린다.
 *
 * ## ⓑ 의 대가를 적어 둔다
 *
 * **한 회차에 시작하는 장이 줄고 미루는 장이 는다.** 미룬 장은 원가가 0 이고 `PENDING`
 * 그대로라 다음 `만들기` 가 집어가지만, **셀러 화면에 그 사실이 보여야** 대가를 치른 것이
 * 된다. 안 보이면 셀러는 만들다 만 작업을 보고 이유를 모른다. 지금 그 자리는
 * `console.warn` 하나뿐이고, 이력 줄과 응답 필드로 드러내는 일은 `coding-agent` 소관이다.
 *
 * ⛔ **이 값을 `cutoutServerBudgetMs` 안으로 넣지 마라.** 넣으면 **장당 몫을 예산 함수의
 * 차로 구하던 두 호출부**가 깨진다. `imaging-container.ts` 의 `RESPONSE_DEADLINE_MS` 와
 * `jobs.ts` 의 `perImageMs` 가 `cutoutServerBudgetMs(1) - cutoutServerBudgetMs(0)` 으로
 * 장당 공급자 몫을 구하는데, 여기서 섞으면 그 차가 **공급자 몫 + D1·R2 왕복**이 된다.
 * 그 둘이 재는 것은 "일어선 컨테이너가 답을 줄 때까지"라 D1·R2 와 아무 상관이 없다. 섞이면
 * 504 가 늦게 뜨고, 늦게 뜨는 504 는 그만큼 셀러가 더 붙잡히는 시간이다. **두 함수를 가른
 * 이유가 이것 하나다.** */
export function cutoutOverheadMs(imageCount: number): number {
  const count = normalizeImageCount(imageCount);
  return (
    (CUTOUT_TAIL_ROUNDTRIPS + count * CUTOUT_ROUNDTRIPS_PER_IMAGE) * CUTOUT_ROUNDTRIP_ESTIMATE_MS +
    // 축소·합성 호출은 저장소 왕복이 아니라 컨테이너 CPU 몫이라 위 곱에 안 실린다. **장수에만**
    // 비례한다 — 루프 뒤에는 둘 다 돌지 않으므로 `cutoutOverheadMs(0)` 에는 안 붙는다.
    // ⛔ 둘을 한 상수로 합치지 마라. 전송 꼴이 다르고 틀렸을 때 잃는 것이 다르다(각 상수 머리말).
    count * (RENDITION_CALL_ESTIMATE_MS + COMPOSITION_CALL_ESTIMATE_MS)
  );
}

/** **장 하나를 더 시작할 때 공급자 몫에 얹어 봐야 하는 시간.**
 *
 * 루프의 판단이 `남은 시간 >= 공급자 몫` 이면 틀린다. 장 하나는 공급자 호출만으로 끝나지
 * 않아서, 그 판단으로 시작한 **마지막 장이 자기 부대비용만큼 예산을 넘겨 끝난다.**
 * `남은 시간 >= 공급자 몫 + 이 값` 이어야 안 넘긴다.
 *
 * ⚠️ **넘치는 양은 장수에 비례하지 않는다.** 앞선 장들의 부대비용은 시계가 이미 삼켜서
 * (루프가 `Date.now()` 를 다시 보므로) 그만큼 뒤 장이 미뤄지는 것으로 정산된다. 정산되지
 * 않고 남는 것은 **마지막 장 하나 몫과 루프 뒤 몫**뿐이다. 그래서 고칠 것도 그 둘이고,
 * 이 함수와 `cutoutOverheadMs(0)` 이 정확히 그 둘이다.
 *
 * **예산 함수의 차로 구한다.** 호출부가 상수를 곱하지 않게 하려는 것이고, `jobs.ts` 가
 * `perImageMs` 를 구하는 어법과 같다.
 *
 * ✅ **축소본이 붙을 때도 합성이 붙을 때도 이 함수는 한 글자도 안 고쳤다**(2026-09-19 두 번).
 * 차로 구하니 새 항이 저절로 따라왔다: 2,500 → `+ RENDITION` 4,000 →
 * `+ 왕복 하나 + COMPOSITION` 7,250ms를 거쳐, 2026-09-20 Modal 축소 시한 교정으로 10,750ms,
 * 같은 날 Modal 합성 시한 교정으로 18,750ms, 2026-10-25 합성본 호환 거울 put 으로 현재
 * **19,000ms**(`3,000 + 5,000 + 11,000`)다.
 * **호출부가 상수를 곱하지 않게 한다**는 이 파일의 어법이 여기서 두 번 값을 했다. */
export function cutoutPerImageOverheadMs(): number {
  return cutoutOverheadMs(1) - cutoutOverheadMs(0);
}

/** **앱이 기다리는 상한.** 자기 숫자를 갖지 않고 서버 예산에서 파생된다.
 *
 * ⛔ **이 함수에 독립된 상수를 들이지 마라.** 예전 앱의 `PROCESS_TIMEOUT_BASE_MS`(30초)가
 * 바로 그 독립된 숫자였고, 서버의 45초와 아무 관계가 없어서 1장짜리 요청조차 서버보다
 * 먼저 끊었다. 여기에 더해도 되는 것은 **왕복 여유 하나**뿐이다. */
export function cutoutClientTimeoutMs(imageCount: number): number {
  return cutoutServerBudgetMs(imageCount) + CUTOUT_CLIENT_SLACK_MS;
}
